import { delay } from '../../mock-utils'
import { ID_TYPE } from '../../reserva/mock'
import { spainId } from '../rules'
import type { GuestField } from '../types'
import type { MiDniExtractedData } from './midni/midniQrData'
import { CaptureError } from './types'
import type { DocumentCapture, DocumentEvent, FaceEvent, QrCapture, Side } from './types'

const TEST = import.meta.env.MODE === 'test'
const STEP_MS = TEST ? 0 : 700
const MOCK_IMAGE = new Blob([], { type: 'image/png' })

/** Imagen mínima para simular la foto enviada (1×1 px). */
export const MOCK_PHOTO = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg=='

/**
 * Captura simulada para desarrollo sin cámara ni claves de MobbScan (VITE_MOCK_CHECKIN=true).
 * Códigos: `sin-camara` (permiso denegado), `lectura-falla` (documento ilegible), `cara-no-coincide`,
 * `menor` (la lectura es de un menor). Lee a Marta (titular) o a Jorge (acompañante) con el número de
 * soporte sin leer, para ver el aviso de campo dudoso de 3b.
 */
export function createMockCapture(guestCode: string): DocumentCapture {
  let onDoc: ((e: DocumentEvent) => void) | null = null
  let onFace: ((e: FaceEvent) => void) | null = null
  let needed: Side[] = []
  const captured = new Set<Side>()
  let face = false

  const take = (side: Side, fromFile: boolean) => {
    onDoc?.({ type: 'capturing', side })
    void delay(STEP_MS).then(() => {
      captured.add(side)
      onDoc?.({ type: 'captured', side, fromFile, image: MOCK_IMAGE })
    })
  }

  return {
    startDocument({ documentType }, onEvent) {
      onDoc = onEvent
      needed = documentType.both ? ['front', 'back'] : documentType.front ? ['front'] : ['back']
      captured.clear()
      void delay(TEST ? 0 : 300).then(() => onDoc?.(guestCode.includes('sin-camara') ? { type: 'error', code: 'no_camera' } : { type: 'ready' }))
    },
    capture: (side) => take(side, false),
    showSide: () => undefined,
    pickFile: (side) => take(side, true),
    giveFile(side) {
      take(side, true)
      return true
    },
    missingSides: () => needed.filter((s) => !captured.has(s)),
    async read({ stay, current, persona }) {
      await delay(TEST ? 0 : 1200)
      if (guestCode.includes('lectura-falla')) throw new CaptureError('unreadable')
      const minor = guestCode.includes('menor')
      const spain = spainId(stay) ?? current.countryId
      const person = minor
        ? { name: 'Lucas', surname1: 'Ruiz', surname2: 'Pérez', identifier: '', bornDate: '2014-05-20', sexId: 'S2' }
        : persona === 'holder'
          ? { name: 'Marta', surname1: 'Ruiz', surname2: 'García', identifier: '12345678Z', bornDate: '1996-04-12', sexId: 'S1' }
          : { name: 'Jorge', surname1: 'Molina', surname2: 'Paz', identifier: '71234567L', bornDate: '1993-11-03', sexId: 'S2' }
      const guest = { ...current, ...person, identifierTypeId: ID_TYPE.dni, countryId: current.countryId || spain, supportNumber: '' }
      const doubtful: GuestField[] = stay.registration.rules.ministerioInterior && !minor ? ['supportNumber'] : []
      const attachments = { front: MOCK_PHOTO, ...(needed.includes('back') ? { back: MOCK_PHOTO } : {}) }
      return { guest, doubtful, attachments, rawData: { documentType: 'ESP', name: person.name.toUpperCase(), mock: true } }
    },
    startFace(onEvent) {
      onFace = onEvent
      face = false
      void delay(TEST ? 0 : 300).then(() => (guestCode.includes('sin-camara') ? onFace?.({ type: 'error', code: 'no_camera' }) : undefined))
    },
    captureFace() {
      onFace?.({ type: 'capturing' })
      void delay(STEP_MS).then(() => {
        face = true
        onFace?.({ type: 'captured' })
      })
    },
    async validateFace() {
      await delay(TEST ? 0 : 1200)
      if (!face) throw new CaptureError('unreadable', 'no face')
      return { valid: !guestCode.includes('cara-no-coincide'), face: MOCK_PHOTO }
    },
    stop() {
      onDoc = null
      onFace = null
    },
  }
}

/** MiDNI simulado: "lee" el QR de Marta al cabo de un momento. */
export function createMockQrCapture(guestCode: string): QrCapture {
  return {
    start(_video, reserveId, onDetected, onError) {
      let active = true
      void delay(TEST ? 0 : 1500).then(() => {
        if (!active) return
        if (guestCode.includes('sin-camara')) return onError('no_camera')
        const data: MiDniExtractedData = {
          reserveId,
          name: 'MARTA',
          surname1: 'RUIZ',
          surname2: 'GARCIA',
          identifier: '12345678Z',
          bornDate: '12-04-1996',
          sexId: 'F',
          supportNumber: 'CAA123456',
          address: 'CALLE ARAGO 120 3 2',
          city: 'BARCELONA',
          rawData: { documentType: 'IDCard', gender: 'F', region: 'BARCELONA', city: 'BARCELONA', verified: false },
        }
        onDetected(data)
      })
      return Promise.resolve(() => {
        active = false
      })
    },
  }
}
